import type { Project } from '@/api';
import type { ViewKey } from './Sidebar';
import { typeMeta } from '../projectTypes';
import { relativeTime } from '../format';
import type { BreakdownRow, MonthUsage } from '../usage';

export default function OverviewView({
  credits,
  thisMonth,
  hosted,
  months,
  breakdown,
  projects,
  scores,
  onNavigate,
}: {
  credits: number;
  thisMonth: number;
  hosted: number;
  months: MonthUsage[];
  breakdown: BreakdownRow[];
  projects: Project[];
  scores: Record<string, number>;
  onNavigate: (v: ViewKey) => void;
}) {
  const max = Math.max(1, ...months.map((m) => m.credits));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-surface border border-border rounded-xl p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-texts">
            <i className="ri-flashlight-line text-accent" />
            Credit balance
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-4xl font-mono text-textp">{credits}</span>
            <span className="text-sm text-texts">credits</span>
          </div>
          <button
            onClick={() => onNavigate('credits')}
            className="whitespace-nowrap mt-4 flex items-center gap-1.5 text-sm font-medium text-accent-text bg-accent hover:bg-accent-hover px-4 py-2 rounded-lg transition-colors"
          >
            Buy credits
            <i className="ri-arrow-right-line" />
          </button>
        </div>

        <div className="bg-surface border border-border rounded-xl p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-texts">
            <i className="ri-bar-chart-2-line text-texts" />
            This month
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-4xl font-mono text-textp">{thisMonth.toLocaleString()}</span>
            <span className="text-sm text-texts">credits used</span>
          </div>
          <div className="mt-4 h-2 rounded-full bg-surface2 overflow-hidden">
            <div
              className="h-full bg-accent rounded-full"
              style={{ width: `${Math.round((thisMonth / (max * 1.4)) * 100)}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-texts">Hosted usage this month vs. your peak</p>
        </div>

        <div className="bg-surface border border-border rounded-xl p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-texts">
            <i className="ri-scales-3-line text-success" />
            Hosted scores
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-4xl font-mono text-textp">{hosted.toLocaleString()}</span>
            <span className="text-sm text-texts">credits</span>
          </div>
          <p className="mt-4 text-sm text-texts">Lifetime hosted scoring and improvement loops.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-surface border border-border rounded-xl p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-textp">Recent projects</h2>
            <button
              onClick={() => onNavigate('projects')}
              className="whitespace-nowrap text-sm text-accent hover:text-accent-hover transition-colors"
            >
              View all
            </button>
          </div>
          {projects.length === 0 ? (
            <p className="mt-4 text-sm text-texts">No playbooks yet. Start a new playbook or run a certified one.</p>
          ) : (
            <div className="mt-4 divide-y divide-border">
              {projects.slice(0, 3).map((p) => {
                const type = typeMeta(p.outcome_type);
                const score = p.outcome_id ? scores[p.outcome_id] : undefined;
                return (
                  <div key={p.id} className="py-3 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-9 h-9 shrink-0 rounded-lg bg-accentSoft border border-accentLine flex items-center justify-center">
                        <i className={`${type.icon} text-accent`} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-textp truncate">{p.title}</p>
                        <p className="text-xs text-texts truncate">{relativeTime(p.updated_at)}</p>
                      </div>
                    </div>
                    <span className="shrink-0 text-sm font-mono text-success">
                      {score == null ? '—' : `${score}/12`}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-surface border border-border rounded-xl p-5">
          <h2 className="text-base font-semibold text-textp">Usage by activity</h2>
          <div className="mt-4 space-y-3">
            {breakdown.map((u) => (
              <div key={u.label} className="flex items-center justify-between">
                <div className="flex items-center gap-2.5 min-w-0">
                  <i
                    className={`${u.icon} ${
                      u.tone === 'success'
                        ? 'text-success'
                        : u.tone === 'accent'
                          ? 'text-accent'
                          : u.tone === 'warning'
                            ? 'text-warning'
                            : 'text-texts'
                    }`}
                  />
                  <span className="text-sm text-texts truncate">{u.label}</span>
                </div>
                <span className="text-sm font-mono text-textp">
                  {u.credits === 0 && u.label === 'Local schema tests' ? 'Free' : u.credits.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
